/**
 * TICKET TEMPLATES — a ticket face composed in the printer's own commands (see masung-printer/layout.ts).
 *
 * A template turns one admission (the same MasungTicketData the till prints from) into printed lines,
 * deciding sizes and wrapping against the real print width. It never stretches type to fit: it picks
 * the largest size on a fixed scale at which the text wraps into the lines it is allowed, and leaves
 * out a field that is empty rather than printing a dash.
 *
 * Designed and compared on the board at ~/printer/ticket-designs (native.js); "Classic" is the
 * original fixed text layout, kept for printers and stock it already suits.
 */
// @ts-expect-error — qrcode ships no types; only QRCode.create is used.
import QRCode from 'qrcode';
import { CELL, cellWidth, lineHeight, opHeight, type Font, type LayoutOp } from '../masung-printer/layout';
import type { MasungTicketData } from '../masung-printer/printer';
import type { PrinterProfile } from '../masung-printer/profile';

export type TemplateId = 'programme-ledger' | 'classic';
export const DEFAULT_TEMPLATE: TemplateId = 'programme-ledger';

type Text = Extract<LayoutOp, { k: 'text' }>;
type Size = { w: number; h: number; font?: Font; track?: number };

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v)).trim();
const U = (v: unknown) => str(v).toUpperCase();
/** "Gate 3" under the label GATE prints as "3"; "HOSPITALITY BOXES" stays itself. */
const unlabel = (label: string, v: unknown) => str(v).replace(new RegExp(`^${label}\\s+`, 'i'), '');

function compose(width: number) {
  const cols = (font: Font = 'A', w = 1, track = 0) => Math.floor(width / cellWidth(font, w, track));
  /** Word wrap to `n` columns; null when one word alone can't fit (the size is wrong, not the word). */
  const wrap = (text: string, n: number, sep = ' ', join = ' ') => {
    const out: string[] = []; let cur = '';
    for (const w of text.split(sep).map((s) => s.trim()).filter(Boolean)) {
      if (w.length > n) return null;
      const t = cur ? cur + join + w : w; if (t.length <= n) cur = t; else { out.push(cur); cur = w; }
    }
    if (cur) out.push(cur); return out;
  };
  /** The first size on the scale at which the text fits in `maxLines`. */
  const fit = (text: string, sizes: Size[], maxLines: number, base: Partial<Text> = {}, sep?: string, join?: string): Text[] => {
    for (const s of sizes) {
      const lines = wrap(text, cols(s.font ?? base.font ?? 'A', s.w, s.track ?? base.track ?? 0), sep, join);
      if (lines && lines.length <= maxLines) return lines.map((l) => ({ k: 'text', ...base, ...s, text: l }));
    }
    const s = sizes[sizes.length - 1]; const n = cols(s.font ?? base.font ?? 'A', s.w);
    return (wrap(text, n, sep, join) ?? [text.slice(0, n)]).map((l) => ({ k: 'text', ...base, ...s, text: l }));
  };
  const T = (text: string, o: Partial<Text> = {}): Text => ({ k: 'text', text, ...o });
  /** Two values on one line, the second at the middle — tab stop in this line's own columns. */
  const pair = (a: string, b: string | undefined, o: Partial<Text>): Text => {
    const half = Math.floor(cols(o.font ?? 'A', o.w ?? 1, o.track ?? 0) / 2);
    return b ? T(`${a}\t${b}`, { ...o, tabs: [half] }) : T(a, o);
  };
  /** Left text and right text on one line, the right one ending at the margin. */
  const spread = (a: string, b: string, o: Partial<Text>): Text => {
    const n = cols(o.font ?? 'A', o.w ?? 1, o.track ?? 0);
    if (a.length + b.length + 1 > n) return T(a, o);
    return T(`${a}\t${b}`, { ...o, tabs: [n - b.length] });
  };
  /** True when `text` wraps into `maxLines` at size `s` with no word too long for the line. */
  const fits = (text: string, s: Size, maxLines: number) => { const l = wrap(text, cols(s.font ?? 'A', s.w, s.track ?? 0)); return !!l && l.length <= maxLines; };
  return { cols, fit, fits, T, pair, spread };
}

/**
 * PROGRAMME + LEDGER — the fixture as the headline, then the steward's facts in labelled columns.
 * Chosen from the board on 2026-10-07: Programme's top, Ledger's body.
 */
export function programmeLedger(t: MasungTicketData, width: number, profile?: PrinterProfile | null): LayoutOp[] {
  const c = compose(width);
  const { fit, fits, T } = c;
  const ops: LayoutOp[] = [];
  // ADAPT TO WHAT THIS PRINTER SHOWED IT DRAWS (setup → Type & columns). Not checked = as the EP802 did.
  const caps = profile?.typeCaps;
  const tall = caps?.tall !== false;
  const columns = caps?.columns !== false;
  const ruleVia = profile?.rasterWorks === false || caps?.rasterRule === false ? (caps?.underlineRule === false ? null : 'underline') : 'raster';
  const rule = (): LayoutOp => (ruleVia ? { k: 'rule', dots: 3, via: ruleVia } : { k: 'gap', dots: 3 });
  const gap = (dots: number): LayoutOp => ({ k: 'gap', dots });
  // The event name in even proportions only — no tall, condensed-looking 2×3 or 1×2 (operator's call,
  // 2026-10-07). A printer that can't draw 3× height starts at 2×2.
  const big: Size[] = (tall ? [{ w: 3, h: 3 }] : []).concat([{ w: 2, h: 2 }, { w: 1, h: 1 }]);
  // No tab stops: the second column goes on its own line rather than wherever the firmware puts it.
  const pair = (a: string, b: string | undefined, o: Partial<Text>): Text[] => (columns || !b ? [c.pair(a, b, o)] : [T(a, o), T(b, o)]);
  const spread = (a: string, b: string, o: Partial<Text>): Text[] => (columns ? [c.spread(a, b, o)] : [T(a, o), T(b, o)]);
  // THE EVENT TITLE — one block, ONE size. With the game's real teams: home, VERSUS, away, both at
  // the largest size where each fits in two lines. Without them, the title exactly as written —
  // never cut at "vs": "Kenya (import test)" is not a team.
  const home = str(t.homeTeam), away = str(t.awayTeam);
  const base = { bold: true, align: 'C' as const, lead: 2 };
  if (home && away) {
    const size = big.find((s) => [home, away].every((n) => fits(U(n), s, 2))) ?? big[big.length - 1];
    ops.push(...fit(U(home), [size], 2, base));
    ops.push(gap(6), T('VERSUS', { font: 'B', align: 'C', track: 9 }), gap(6));
    ops.push(...fit(U(away), [size], 2, base));
  } else ops.push(...fit(U(t.eventName), big, 3, base));

  const when = [U(t.date), str(t.time) && str(t.time) !== '-' ? str(t.time) : ''].filter((s) => s && s !== '-').join(' · ');
  if (when) ops.push(gap(16), ...fit(when, [{ w: 1, h: 1 }], 2, { bold: true, align: 'C' }));
  const doors = str(t.entryWindow);
  if (doors && doors !== '-' && doors !== str(t.time)) ops.push(T(`DOORS ${doors}`, { font: 'B', align: 'C' }));
  if (str(t.venue)) ops.push(...fit(U(t.venue), [{ w: 1, h: 1, font: 'B' }], 2, { align: 'C' }));

  // THE TICKET TYPE — its own band: the steward at a VVIP or media door reads nothing else.
  if (str(t.ticketType)) {
    ops.push(gap(16), rule(), gap(10));
    ops.push(...fit(U(t.ticketType), [{ w: 2, h: 2, track: 4 }, { w: 2, h: 2 }, { w: 1, h: 2 }], 1, { bold: true, align: 'C' }));
    ops.push(gap(4), rule());
  }
  ops.push(gap(14));

  const rows = ([['GATE', unlabel('gate', t.gate) || 'ANY'], ['SECTION', unlabel('section', t.section)], ['ROW', unlabel('row', t.row)], ['SEAT', unlabel('seat', t.seat)]] as [string, string][])
    .filter(([, v]) => v && v !== '-');
  for (let i = 0; i < rows.length; i += 2) {
    const a = rows[i], b = rows[i + 1];
    if ([a, b].some((r) => r && r[1].length > 10)) {
      for (const r of [a, b].filter(Boolean)) ops.push(T(r[0], { font: 'B', bold: true }), ...fit(U(r[1]), [{ w: 2, h: 2 }, { w: 1, h: 2 }], 2, { bold: true }), gap(8));
      continue;
    }
    ops.push(...pair(a[0], b?.[0], { font: 'B', bold: true }), ...pair(U(a[1]), b && U(b[1]), { w: 2, h: 2, bold: true }), gap(10));
  }
  if (!rows.some(([k]) => k === 'SEAT')) ops.push(T(rows.some(([k]) => k === 'SECTION') ? 'STANDING · NO RESERVED SEAT' : 'GENERAL ADMISSION', { font: 'B', bold: true }));

  ops.push(gap(8), rule(), gap(10), ...spread(U(t.attendeeName), str(t.orderId), { bold: true }));
  ops.push(gap(18), { k: 'qr', data: t.qrData, module: 8 }, T('ONE ENTRY · SCAN AT THE TURNSTILE', { font: 'B', align: 'C' }));
  return ops;
}

export const TEMPLATES: { id: TemplateId; name: string; note: string }[] = [
  { id: 'programme-ledger', name: 'Programme + Ledger', note: 'The fixture as the headline, the ticket type in its own band, gate · section · row · seat in columns.' },
  { id: 'classic', name: 'Classic', note: 'The original plain layout.' },
];

/* ── Preview: the same ops at the printer's geometry. Glyph shapes stand in for the ROM font. ── */
const qrModules = (data: string) => QRCode.create(data, { errorCorrectionLevel: 'M' }).modules;
const qrSize = (data: string) => qrModules(data).size as number;
export const layoutLength = (ops: LayoutOp[]) => ops.reduce((y, op) => y + opHeight(op, qrSize), 0);

export function drawLayout(c: HTMLCanvasElement, ops: LayoutOp[], width: number, profile?: PrinterProfile | null) {
  c.width = width; c.height = Math.max(layoutLength(ops), 100);
  const x = c.getContext('2d')!; x.fillStyle = '#fff'; x.fillRect(0, 0, c.width, c.height); x.fillStyle = '#111';
  let y = 0;
  for (const op of ops) {
    if (op.k === 'gap') { y += op.dots; continue; }
    if (op.k === 'rule') { const h = op.via === 'underline' ? 2 : op.dots; x.fillRect(op.inset ?? 0, y + opHeight(op, qrSize) - h, width - 2 * (op.inset ?? 0), h); y += opHeight(op, qrSize); continue; }
    if (op.k === 'nv') { y += op.heightDots; continue; }
    if (op.k === 'raw') { y += op.heightDots; continue; }
    if (op.k === 'qr') {
      const m = qrModules(op.data), n = m.size, s = op.module, left = Math.round((width - n * s) / 2);
      for (let r = 0; r < n; r++) for (let q = 0; q < n; q++) if (m.get(r, q)) x.fillRect(left + q * s, y + r * s, s, s);
      y += n * s + 30; continue;
    }
    const f = op.font ?? 'A', w = op.w ?? 1, h = op.h ?? 1, adv = cellWidth(f, w, op.track ?? 0), lh = lineHeight(op);
    const cells: string[] = []; let col = 0; const stops = op.tabs ?? [];
    for (const ch of op.text) {
      if (ch === '\t') { const next = stops.find((s) => s > col) ?? col + 1; while (col < next) { cells.push(' '); col++; } } else { cells.push(ch); col++; }
    }
    const lineW = cells.length * adv;
    const left = op.align === 'C' ? Math.floor((width - lineW) / 2) : op.align === 'R' ? width - lineW : 0;
    x.font = `${op.bold ? 700 : 400} ${f === 'A' ? 20 : 14.5}px Menlo, monospace`;
    cells.forEach((ch, i) => {
      if (ch === ' ') return;
      x.save(); x.translate(left + i * adv, y + lh - (op.lead ?? 6) - CELL[f].h * h * 0.22); x.scale(w, h); x.fillText(ch, 0, 0); x.restore();
    });
    y += lh;
  }
}
